import React, { useState } from 'react';
import { Book, Bell } from 'lucide-react';
import subscriptionApi from '../services/subscriptionApi';

const BibleStudiesPage = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [error, setError] = useState('');

  const handleNotifySubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      await subscriptionApi.subscribe(email, '', 'bible_study', ['bible_studies', 'ministry_updates']);
      setIsSubscribed(true);
      setEmail('');
    } catch (err) {
      setError('Please try again');
      console.error('Bible study subscription error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <div className="pt-16 lg:pt-20 pb-20 lg:pb-0">
      {/* Hero Section */}
      <section className="py-12 lg:py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 lg:mb-12">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-800 mb-6 font-display">
              Bible Studies
            </h1>
            <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto font-sans">
              Deep exploration of Scripture through systematic study, verse-by-verse teaching, 
              and practical application for Christian living and spiritual growth.
            </p>
          </div>
        </div>
      </section>

      {/* Coming Soon Section */}
      <section className="py-12 lg:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 lg:p-12 shadow-xl">
              <div className="w-24 h-24 bg-[#f17b21] rounded-full flex items-center justify-center mx-auto mb-8">
                <Book className="w-12 h-12 text-white" />
              </div>
              
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-800 mb-6 font-display">
                Bible Studies Coming Soon
              </h2>
              
              <p className="text-lg lg:text-xl text-gray-600 mb-8 leading-relaxed font-sans">
                We are preparing comprehensive Bible study programs that will dive deep into God's Word. 
                These studies will feature verse-by-verse exposition, practical application, and spiritual growth exercises.
              </p>

              <div className="bg-blue-50 border border-blue-100 rounded-xl p-6 mb-8">
                <h3 className="text-xl font-bold text-gray-800 mb-4 font-display">Planned Study Features</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-[#f17b21] rounded-full"></div>
                    <span className="text-gray-700 font-sans">Verse-by-verse exposition</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-[#f17b21] rounded-full"></div>
                    <span className="text-gray-700 font-sans">Practical application guides</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-[#f17b21] rounded-full"></div>
                    <span className="text-gray-700 font-sans">Discussion questions</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-[#f17b21] rounded-full"></div>
                    <span className="text-gray-700 font-sans">Downloadable resources</span>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-[#f17b21] to-[#ff9649] rounded-xl p-6 text-white mb-8">
                <h3 className="text-xl font-bold mb-4 font-display">Get Notified When Available</h3>
                <p className="mb-6 opacity-90 font-sans">
                  Be the first to know when our Bible study programs launch. We'll send you an email 
                  with details about upcoming studies and how to participate.
                </p>
                
                {!isSubscribed ? (
                  <form onSubmit={handleNotifySubmit}>
                    <div className="flex flex-col sm:flex-row gap-4">
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter your email address"
                        className="flex-1 px-4 py-3 rounded-lg text-gray-800 font-sans focus:ring-2 focus:ring-white focus:outline-none"
                        required
                      />
                      <button 
                        type="submit"
                        disabled={isSubmitting}
                        className="bg-white text-[#f17b21] hover:bg-gray-100 px-6 py-3 rounded-lg font-semibold transition-colors duration-300 font-sans flex items-center justify-center space-x-2 disabled:opacity-70"
                      >
                        {isSubmitting ? (
                          <>
                            <div className="animate-spin w-4 h-4 border-2 border-[#f17b21] border-t-transparent rounded-full"></div>
                            <span>Adding...</span>
                          </>
                        ) : (
                          <>
                            <Bell className="w-5 h-5" />
                            <span>Notify Me</span>
                          </>
                        )}
                      </button>
                    </div>
                    {error && (
                      <p className="text-white/80 text-sm mt-2 font-sans">{error}</p>
                    )}
                  </form>
                ) : (
                  <div className="text-center">
                    <div className="inline-flex items-center space-x-2 bg-white/20 px-4 py-2 rounded-lg">
                      <Bell className="w-5 h-5" />
                      <span className="font-sans">You'll be notified when Bible studies launch!</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="text-center">
                <p className="text-gray-600 mb-6 font-sans">
                  In the meantime, explore our other ministry offerings:
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button 
                    onClick={() => window.location.href = '/sermons'}
                    className="bg-[#f17b21] hover:bg-[#d86919] text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-300 font-sans"
                  >
                    Watch Sermons
                  </button>
                  <button 
                    onClick={() => window.location.href = '/blog'}
                    className="border-2 border-[#f17b21] text-[#f17b21] hover:bg-[#f17b21] hover:text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-300 font-sans"
                  >
                    Read Blog Posts
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Scripture Section */}
      <section className="py-12 lg:py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <div className="bg-white rounded-2xl p-6 lg:p-8 shadow-lg">
              <Book className="w-16 h-16 text-[#f17b21] mx-auto mb-6" />
              <h3 className="text-2xl lg:text-3xl font-bold text-gray-800 mb-4 font-display">
                "Study to Show Yourself Approved"
              </h3>
              <p className="text-lg text-gray-700 font-sans italic">
                "Be diligent to present yourself approved to God, a worker who does not need to be ashamed, 
                rightly dividing the word of truth."
              </p>
              <p className="text-[#f17b21] font-semibold mt-2 font-sans">- 2 Timothy 2:15</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BibleStudiesPage;